import { ShieldCheck, Zap, Clock, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/lib/router-compat";

interface TwoWaysProps {
  /**
   * Compact variant renders tighter spacing for embedding inside
   * existing pages (FAQ, cost, etc.) without dominating the section.
   */
  compact?: boolean;
  /**
   * Override the section heading.
   */
  heading?: string;
  /**
   * Override the eyebrow label.
   */
  eyebrow?: string;
}

/**
 * Reusable "Two Ways to Get Started" section presenting both
 * access pathways for SAINT® treatment:
 *   1. Select Insurance Plans — BCBS in select states & BCBS FEP in select states
 *   2. Self-Pay — fastest path, greatest flexibility, 7-day availability
 *
 * Tone is warm, professional, patient-focused. Never overstates
 * insurance coverage ("select states" qualifier always present).
 */
export const TwoWaysToGetStarted = ({
  compact = false,
  heading = "Two ways to get started",
  eyebrow = "Access & Availability",
}: TwoWaysProps) => (
  <section
    className={`${compact ? "py-10 sm:py-12" : "py-16 sm:py-20"} bg-background`}
  >
    <div className="container-px mx-auto max-w-5xl px-4">
      <div className="text-center mb-8 sm:mb-10">
        <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-primary">
          <ShieldCheck className="h-4 w-4 text-accent" />
          {eyebrow}
        </span>
        <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          {heading}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl mx-auto sm:text-base">
          There are two pathways to access SAINT® treatment. We'll help you find
          the fastest route to care during your free consultation.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Pathway 1 — Select Insurance Plans */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <span className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Pathway 1
              </span>
              <h3 className="font-headings text-lg font-bold text-primary">
                Select Insurance Plans
              </h3>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We accept Blue Cross Blue Shield in select states and the Blue Cross
            Blue Shield Federal Employee Program in select states. Because
            insurance requires a review and approval process, this pathway
            generally takes longer from consultation to treatment. Our team
            guides patients through every step — from benefits verification to
            prior authorization and documentation.
          </p>
          <ul className="mt-4 space-y-2">
            <li className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              BCBS accepted in select states
            </li>
            <li className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              BCBS Federal Employee Program in select states
            </li>
            <li className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              We handle verification, authorization & documentation
            </li>
          </ul>
        </div>

        {/* Pathway 2 — Self-Pay (most common) */}
        <div className="rounded-2xl border-2 border-accent/30 bg-accent/[0.04] p-6 shadow-premium sm:p-8 relative">
          <span className="absolute -top-3 right-6 rounded-full bg-gradient-accent px-3 py-1 text-xs font-bold text-accent-foreground shadow-glow">
            Most Common
          </span>
          <div className="flex items-center gap-3 mb-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
              <Zap className="h-5 w-5" />
            </span>
            <div>
              <span className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Pathway 2
              </span>
              <h3 className="font-headings text-lg font-bold text-primary">
                Self-Pay
              </h3>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Our most common option, offering the greatest speed and flexibility.
            Most patients move from consultation to treatment within a few days.
            With our recently expanded facilities, we offer private treatment
            suites, extended care team hours, and appointments seven days a
            week.
          </p>
          <ul className="mt-4 space-y-2">
            <li className="flex items-start gap-2 text-sm text-muted-foreground">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              Consultation to treatment in days, not weeks
            </li>
            <li className="flex items-start gap-2 text-sm text-muted-foreground">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              Appointments available 7 days a week
            </li>
            <li className="flex items-start gap-2 text-sm text-muted-foreground">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              Private treatment suites & extended hours
            </li>
          </ul>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <Link
          to="/saint"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-bold text-white shadow-glow transition-opacity hover:opacity-90 sm:w-auto"
        >
          Find Your Fastest Path to Care
          <ArrowRight className="h-4 w-4" />
        </Link>
        <a
          href="tel:9548279793"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-border bg-card px-6 py-3.5 text-sm font-bold text-primary transition-colors hover:bg-muted sm:w-auto"
        >
          <Phone className="h-4 w-4" />
          Call 954-827-9793
        </a>
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        Insurance acceptance varies by state and plan. Coverage is not
        guaranteed. Contact us to determine eligibility and the fastest path to
        care for your situation.
      </p>
    </div>
  </section>
);

export default TwoWaysToGetStarted;
